import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Sparkles } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { ASSETS, img } from "@/lib/api";

export default function Nav() {
  const { setOpen, totals } = useCart();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  const goldenTicketJump = () => {
    const scrollToGT = () => {
      const el = document.getElementById("golden-ticket");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (loc.pathname !== "/") {
      navigate("/#golden-ticket");
      // Wait for landing to mount then scroll.
      setTimeout(scrollToGT, 350);
    } else {
      scrollToGT();
    }
  };

  const links = [
    { to: "/shop", label: "Shop All" },
    { to: "/drops/BASQO", label: "BASQO" },
    { to: "/drops/BASQET", label: "BASQET" },
    { to: "/drops/FIFTYTWO", label: "FIFTYTWO" },
    { to: "/#golden-ticket", label: "Golden Ticket" },
  ];

  return (
    <header
      className="sticky top-0 z-40 border-b border-bone-10"
      style={{ background: "rgba(11,11,11,0.82)", backdropFilter: "blur(18px)" }}
      data-testid="site-nav"
    >
      {/* Pre-launch announcement strip — visible on every page until we flip to live payments */}
      <button
        type="button"
        onClick={goldenTicketJump}
        className="block w-full bg-imperial text-obsidian hover:bg-bone transition-colors text-left"
        data-testid="coming-soon-bar"
        aria-label="Site coming soon — join the Golden Ticket list"
      >
        <div className="max-w-[1440px] mx-auto flex items-center justify-center gap-x-2 gap-y-0 px-4 py-2 font-mono text-[10px] sm:text-[11px] tracking-[0.22em] sm:tracking-[0.25em] uppercase flex-wrap">
          <span className="flex items-center gap-2">
            <Sparkles className="w-3 h-3 shrink-0" />
            <span className="font-bold">Coming Soon</span>
          </span>
          <span className="hidden sm:inline opacity-80">· Site goes live any day now ·</span>
          <span className="sm:hidden opacity-60">·</span>
          <span className="underline underline-offset-4 font-semibold">Join the Golden Ticket →</span>
        </div>
      </button>

      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-6 lg:px-12 py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <img src={ASSETS.logo} alt="GrailSwag" className="h-11 w-11 object-contain" />
          <span className="font-display text-xl tracking-tight text-bone hidden sm:block">GRAIL<span className="text-imperial">SWAG</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="font-mono text-[11px] tracking-[0.25em] uppercase text-bone/80 hover:text-imperial transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 text-bone hover:text-imperial transition-colors"
            data-testid="nav-cart-button"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            <span className="font-mono text-xs tracking-wider">{totals.totalQty}</span>
          </button>
          <button
            className="lg:hidden text-bone"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-bone-10 bg-obsidian" data-testid="nav-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="font-mono text-xs tracking-[0.25em] uppercase text-bone/90"
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Mobile-only drop-print teaser row: three graffiti drop artworks with radial-fade masking
         so the light image edges blend seamlessly into the black header. Hidden on desktop. */}
      <div
        className="lg:hidden border-t border-bone-10 bg-obsidian overflow-hidden"
        data-testid="mobile-drop-print-row"
      >
        <div className="grid grid-cols-3 gap-1 max-w-md mx-auto py-2 px-2">
          {[
            { id: "BASQO", src: ASSETS.printBasqo },
            { id: "BASQET", src: ASSETS.printBasqet },
            { id: "FIFTYTWO", src: ASSETS.printFiftytwo },
          ].map((p) => (
            <Link
              key={p.id}
              to={`/drops/${p.id}`}
              className="relative aspect-square overflow-hidden group"
              data-testid={`mobile-print-${p.id.toLowerCase()}`}
              aria-label={`${p.id} drop`}
            >
              <img
                src={img(p.src, 320)}
                alt={`${p.id} drop art`}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-center mask-radial-fade scale-105 group-active:scale-110 transition-transform duration-500"
              />
              <span className="pointer-events-none absolute bottom-1 inset-x-0 text-center font-mono text-[9px] tracking-[0.25em] uppercase text-imperial">
                {p.id}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
}
